import { Controller, useFormContext } from 'react-hook-form';

import type { BusinessDetailsFormValues } from '~domains/partner-application/entities/application';
import { getCategoryTitlesMap } from '~domains/partner-application/entities/application/lib';
import { useCategories } from '~domains/partner-application/entities/company-category';

import { Select } from '@breezeventures/ui-kit/select/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';

export const SelectCompanyCategory = () => {
  const formContext = useFormContext<BusinessDetailsFormValues>();
  const { data, isLoading, isError } = useCategories();

  const categories = data?.categories || [];

  const categoryOptions = [...categories]
    .sort((a, b) => {
      const isOtherA = a.description === 'Other';
      const isOtherB = b.description === 'Other';

      return Number(isOtherA) - Number(isOtherB);
    })
    .map((category) => ({
      value: category.id,
      label: category.description,
    }));

  const categoryTitlesMap = getCategoryTitlesMap(categories);

  const defaultRenderValue = (values: string[] = []) =>
    values.map((value) => categoryTitlesMap[value] || value).join(', ');

  const companyCategories = formContext.watch('companyCategories');
  const showOtherCategoryInput = !!companyCategories?.some(
    (val) => categoryTitlesMap[val] === 'Other'
  );

  return (
    <>
      <Controller
        name="companyCategories"
        control={formContext?.control}
        render={(props) => (
          <Select
            {...props}
            field={{
              ...props.field,
              onChange: (value) => {
                props.field.onChange(value ?? []);
              },
            }}
            label="Product category"
            showClear
            multiple
            disabled={isLoading || isError}
            renderValue={defaultRenderValue}
          >
            {isError ? (
              <Select.Option value="" disabled>
                <Typography.Text>Failed to load categories</Typography.Text>
              </Select.Option>
            ) : (
              categoryOptions.map(({ value, label: optionLabel }) => (
                <Select.Option key={value} value={value}>
                  <Typography.Text>{optionLabel}</Typography.Text>
                  <Select.OptionIndicator multiple />
                </Select.Option>
              ))
            )}
          </Select>
        )}
      />
      {showOtherCategoryInput && (
        <Controller
          shouldUnregister
          name="companyCategoryOther"
          control={formContext?.control}
          render={(props) => (
            <TextField {...props} label="Enter your product category" />
          )}
        />
      )}
    </>
  );
};
